import { useState } from "react";

import {
  IBatchIdentityPreviewItem,
  IManualIdentityPreview,
} from "@/@types/divergence";
import { MIN_INSTALLMENTS } from "@/constants/payroll-installments";
import {
  useLazyPreviewManualIdentityQuery,
  usePreviewIdentitiesBatchQuery,
} from "@/services/divergence";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { identityChoiceIsComplete } from "../utils/manual-identity";
import { DECISION_LABELS } from "../utils/messages";

export interface BatchRowState {
  installments: number;
  ruleAckedFor: string;
  employmentAckedFor: string;
  cpf: string;
  chosen: string;
  informed: IManualIdentityPreview | null;
  searchError: string | null;
  isSearching: boolean;
  candidatesFailed: boolean;
}

export interface BatchRow {
  id: number;
  resolved: IBatchIdentityPreviewItem | null;
  state: BatchRowState;
  effective: IManualIdentityPreview | null;
  needsRuleAck: boolean;
  ruleAcked: boolean;
  ready: boolean;
}

export interface BatchApprovalState {
  rows: Map<number, BatchRow>;
  isLoading: boolean;
  loadError: string | null;
  readyCount: number;
  allRuleAcked: boolean;
  setInstallments: (id: number, value: number) => void;
  setAllInstallments: (value: number) => void;
  setCpf: (id: number, value: string) => void;
  setChosen: (id: number, value: string) => void;
  chooseCandidate: (id: number, operatorCode: string) => Promise<void>;
  ackRule: (id: number, value: boolean) => void;
  ackEmployment: (id: number, cpf: string) => void;
  ackAllRules: (value: boolean) => void;
  search: (id: number) => Promise<void>;
}

const emptyState = (installments?: number): BatchRowState => ({
  installments: installments ?? MIN_INSTALLMENTS,
  ruleAckedFor: "",
  employmentAckedFor: "",
  cpf: "",
  chosen: "",
  informed: null,
  searchError: null,
  isSearching: false,
  candidatesFailed: false,
});

export function useBatchApproval(ids: number[]): BatchApprovalState {
  const [states, setStates] = useState<Record<number, BatchRowState>>({});

  const {
    data,
    error,
    isFetching: isLoading,
  } = usePreviewIdentitiesBatchQuery(
    { divergence_ids: ids },
    { skip: ids.length === 0 },
  );

  const [runPreview] = useLazyPreviewManualIdentityQuery();

  const patch = (id: number, change: Partial<BatchRowState>): void => {
    setStates((current: Record<number, BatchRowState>) => ({
      ...current,
      [id]: { ...(current[id] ?? emptyState()), ...change },
    }));
  };

  const rows: Map<number, BatchRow> = new Map(
    ids.map((id: number): [number, BatchRow] => {
      const resolved: IBatchIdentityPreviewItem | null =
        (data ?? []).find(
          (item: IBatchIdentityPreviewItem): boolean =>
            item.divergence_id === id,
        ) ?? null;

      const state: BatchRowState = states[id] ?? emptyState();

      const effective: IManualIdentityPreview | null =
        state.informed ?? resolved?.preview ?? null;

      const needsRuleAck: boolean =
        !state.informed &&
        Boolean(resolved?.preview) &&
        resolved?.source !== "INFORMED";

      const ruleAcked: boolean =
        ruleAckedOf(state, resolved?.preview ?? null) || !needsRuleAck;

      const ready: boolean =
        ruleAcked &&
        identityChoiceIsComplete({
          candidates: resolved?.identity_candidates ?? [],
          preview: effective,
          chosen: state.chosen,
          acknowledgedFor: state.employmentAckedFor,
        });

      return [id, { id, resolved, state, effective, needsRuleAck, ruleAcked, ready }];
    }),
  );

  const pending: BatchRow[] = [...rows.values()].filter(
    (row: BatchRow): boolean => row.needsRuleAck,
  );

  const lookup = async (
    id: number,
    query: { cpf?: string; operator_code?: string },
    optionals: { asCandidate?: boolean } = {},
  ): Promise<void> => {
    patch(id, { isSearching: true, searchError: null });

    try {
      const found: IManualIdentityPreview = await runPreview({
        divergence_id: id,
        ...query,
      }).unwrap();

      patch(id, { informed: found, isSearching: false });
    } catch (searchError) {
      patch(id, {
        informed: null,
        isSearching: false,
        candidatesFailed: Boolean(optionals.asCandidate),
        searchError: extractRtkErrorMessage(searchError, DECISION_LABELS.ERROR),
      });
    }
  };

  return {
    rows,
    isLoading,
    loadError: error
      ? extractRtkErrorMessage(error, DECISION_LABELS.ERROR)
      : null,
    readyCount: [...rows.values()].filter((row: BatchRow): boolean => row.ready)
      .length,
    allRuleAcked:
      pending.length > 0 &&
      pending.every((row: BatchRow): boolean => row.ruleAcked),
    setInstallments: (id: number, value: number): void =>
      patch(id, { installments: value }),
    setAllInstallments: (value: number): void =>
      setStates((current: Record<number, BatchRowState>) => {
        const next: Record<number, BatchRowState> = { ...current };

        ids.forEach((id: number): void => {
          next[id] = {
            ...(current[id] ?? emptyState()),
            installments: value,
          };
        });

        return next;
      }),
    setCpf: (id: number, value: string): void => patch(id, { cpf: value }),
    setChosen: (id: number, value: string): void => patch(id, { chosen: value }),
    ackRule: (id: number, value: boolean): void =>
      patch(id, {
        ruleAckedFor: value
          ? (rows.get(id)?.resolved?.preview?.identity.cpf ?? "")
          : "",
      }),
    ackEmployment: (id: number, cpf: string): void =>
      patch(id, { employmentAckedFor: cpf }),
    ackAllRules: (value: boolean): void =>
      setStates((current: Record<number, BatchRowState>) => {
        const next: Record<number, BatchRowState> = { ...current };

        pending.forEach((row: BatchRow): void => {
          next[row.id] = {
            ...(current[row.id] ?? emptyState()),
            ruleAckedFor: value
              ? (row.resolved?.preview?.identity.cpf ?? "")
              : "",
          };
        });

        return next;
      }),
    search: async (id: number): Promise<void> => {
      const row: BatchRow | undefined = rows.get(id);

      if (!row) return;

      await lookup(id, { cpf: row.state.cpf });
    },
    chooseCandidate: async (id: number, operatorCode: string): Promise<void> => {
      patch(id, { chosen: operatorCode });

      await lookup(id, { operator_code: operatorCode }, { asCandidate: true });
    },
  };
}

const ruleAckedOf = (
  state: BatchRowState,
  preview: IManualIdentityPreview | null,
): boolean => Boolean(preview) && state.ruleAckedFor === preview?.identity.cpf;
